import { useState, useEffect, useCallback } from "react";
import { base44 } from "../api/base44Client";
import {
  Button,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Container,
  AppBar,
  Toolbar,
  Chip,
} from "@mui/material";
import {
  Shield,
  CheckCircle2,
  Clock,
  AlertTriangle,
  XCircle,
  ChevronRight,
  Lock,
} from "lucide-react";
import {
  calculateCompletion,
  getMissingFields,
  STEPS,
} from "../lib/stepConfig";
import ProgressHeader from "../components/financing/ProgressHeader";
import StepIndicator from "../components/financing/StepIndicator";
import StepNavigation from "../components/financing/StepNavigation";
import Step1Pratica from "../components/financing/steps/Step1Pratica";
import Step2Soggetto from "../components/financing/steps/Step2Soggetto";
import Step3Anagrafica from "../components/financing/steps/Step3Anagrafica";
import Step4Residenza from "../components/financing/steps/Step4Residenza";
import Step5Recapiti from "../components/financing/steps/Step5Recapiti";
import Step6Documento from "../components/financing/steps/Step6Documento";
import Step7Cittadinanza from "../components/financing/steps/Step7Cittadinanza";
import Step8Occupazione from "../components/financing/steps/Step8Occupazione";
import Step9Datore from "../components/financing/steps/Step9Datore";
import Step10Reddito from "../components/financing/steps/Step10Reddito";
import Step11SocioDemografico from "../components/financing/steps/Step11SocioDemografico";
import Step12Pagamento from "../components/financing/steps/Step12Pagamento";
import Step13Conferma from "../components/financing/steps/Step13Conferma";
import { motion } from "framer-motion";
import moment from "moment";

function getToken() {
  const path = window.location.pathname;
  const match = path.match(/\/intake\/([a-f0-9]{64})/);
  return match ? match[1] : null;
}

function WelcomeScreen({ data, missingCount, onStart }) {
  const name = data.nome || "Cliente";
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            bgcolor: "primary.main",
            px: 3,
            pt: 8,
            pb: 12,
            textAlign: "center",
            color: "primary.contrastText",
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <Shield size={32} color="white" />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
            Ciao {name}!
          </Typography>
          <Typography
            variant="body2"
            sx={{ opacity: 0.8, maxWidth: 300, mx: "auto" }}
          >
            Completa la tua richiesta di finanziamento polizza in pochi minuti
          </Typography>
        </Box>

        <Container maxWidth="xs" sx={{ mt: -5, flex: 1, pb: 4 }}>
          <Paper elevation={4} sx={{ p: 3, borderRadius: 4, mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  bgcolor: "success.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CheckCircle2 size={20} color="white" />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Dati già inseriti
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Controlla e completa i dati mancanti
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {[
                {
                  icon: Clock,
                  text: `Solo ${
                    missingCount > 0
                      ? missingCount + " campi mancanti"
                      : "la conferma finale"
                  }`,
                  sub: "Richiede pochi minuti",
                },
                {
                  icon: Lock,
                  text: "Dati protetti",
                  sub: "Connessione sicura",
                },
                {
                  icon: CheckCircle2,
                  text: "Salvataggio automatico",
                  sub: "Puoi riprendere quando vuoi",
                },
              ].map(({ icon: Icon, text, sub }) => (
                <Box
                  key={text}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                    p: 1.5,
                    bgcolor: "action.hover",
                    borderRadius: 3,
                  }}
                >
                  <Icon size={16} style={{ marginTop: 2, color: "#1976d2" }} />
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, display: "block" }}
                    >
                      {text}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", fontSize: "0.625rem" }}
                    >
                      {sub}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>

          {data.importo_finanziare && (
            <Paper
              variant="outlined"
              sx={{ p: 2, borderRadius: 3, mb: 3, bgcolor: "background.paper" }}
            >
              <Typography variant="caption" color="text.secondary">
                Importo da finanziare
              </Typography>
              <Typography
                variant="h5"
                sx={{ fontWeight: 800, color: "primary.main" }}
              >
                € {Number(data.importo_finanziare).toLocaleString("it-IT")}
              </Typography>
              {data.prodotto_compagnia && (
                <Typography
                  variant="caption"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {data.prodotto_compagnia}
                </Typography>
              )}
            </Paper>
          )}

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={onStart}
            endIcon={<ChevronRight size={20} />}
            sx={{
              textTransform: "none",
              borderRadius: 3,
              py: 1.5,
              fontWeight: 700,
              fontSize: "1rem",
            }}
          >
            Inizia ora
          </Button>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              textAlign: "center",
              display: "block",
              mt: 3,
              px: 2,
              fontSize: "0.625rem",
            }}
          >
            Per proseguire è necessario prendere visione dell'informativa
            privacy
          </Typography>
        </Container>
      </Box>
    </motion.div>
  );
}

function ErrorScreen({ code, expiresAt }) {
  const messages = {
    revoked: {
      icon: XCircle,
      title: "Link non più valido",
      body: "Questo link è stato revocato. Contatta il tuo agente per riceverne uno nuovo.",
      color: "#d32f2f",
    },
    expired: {
      icon: Clock,
      title: "Link scaduto",
      body: `Questo link era valido fino al ${
        expiresAt ? moment(expiresAt).format("DD/MM/YYYY") : ""
      }. Contatta il tuo agente per riceverne uno nuovo.`,
      color: "#ed6c02",
    },
    notfound: {
      icon: AlertTriangle,
      title: "Link non trovato",
      body: "Il link inserito non è valido. Verifica di aver copiato correttamente l'indirizzo.",
      color: "#9e9e9e",
    },
  };
  const cfg = messages[code] || messages.notfound;
  const Icon = cfg.icon;
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 4,
      }}
    >
      <Box sx={{ textAlign: "center", maxWidth: 320 }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            bgcolor: "action.hover",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 3,
          }}
        >
          <Icon size={32} style={{ color: cfg.color }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          {cfg.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {cfg.body}
        </Typography>
      </Box>
    </Box>
  );
}

function SubmittedScreen({ data }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
          p: 4,
        }}
      >
        <Box sx={{ textAlign: "center", maxWidth: 360 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: "success.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 4,
            }}
          >
            <CheckCircle2 size={40} color="white" />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
            Richiesta completata!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            La tua richiesta di finanziamento è stata inviata con successo.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Riceverai una conferma dal tuo agente assicurativo.
          </Typography>
          <Paper
            variant="outlined"
            sx={{ p: 2, borderRadius: 3, textAlign: "left" }}
          >
            <Typography
              variant="caption"
              sx={{
                textTransform: "uppercase",
                letterSpacing: 0.5,
                color: "text.secondary",
                display: "block",
              }}
            >
              Numero pratica
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{ fontFamily: "monospace", fontWeight: 600 }}
            >
              {data?.pratica_id || "—"}
            </Typography>
          </Paper>
        </Box>
      </Box>
    </motion.div>
  );
}

export default function CustomerIntake() {
  const [phase, setPhase] = useState("loading");
  const [errorCode, setErrorCode] = useState(null);
  const [linkData, setLinkData] = useState(null);
  const [formData, setFormData] = useState({});
  const [metadata, setMetadata] = useState({});
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  const token = getToken();

  useEffect(() => {
    async function init() {
      if (!token) {
        setErrorCode("notfound");
        setPhase("error");
        return;
      }
      const res = await base44.functions.invoke("validateCustomerLink", {
        token,
      });
      const d = res.data;
      if (!d.success) {
        setErrorCode(d.code || "notfound");
        setPhase("error");
        return;
      }
      setLinkData(d.link);
      if (d.link.status === "submitted" || d.readonly) {
        setFormData(d.data || {});
        setPhase("submitted");
        return;
      }
      const record = d.data || {};
      setFormData(record);
      if (record.fields_metadata) setMetadata(record.fields_metadata);
      setCurrentStep(record.current_step || 1);
      setPhase("welcome");
    }
    init();
  }, [token]);

  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      const log = updated.log_modificati || [];
      if (!log.includes(field)) {
        updated.log_modificati = [...log, field];
      }
      updated.fields_metadata = {
        ...updated.fields_metadata,
        [field]: {
          ...(updated.fields_metadata?.[field] || {}),
          source: "cliente",
          completed_by: "cliente",
        },
      };
      return updated;
    });
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  const saveDraft = useCallback(
    async (data) => {
      if (!token) return;
      setIsSaving(true);
      const toSave = {
        ...data,
        current_step: currentStep,
        completion_percentage: calculateCompletion(data),
        campi_mancanti: getMissingFields(data),
      };
      await base44.functions.invoke("submitCustomerIntake", {
        token,
        form_data: toSave,
        is_draft: true,
      });
      setLastSaved(moment().format("HH:mm"));
      setIsSaving(false);
    },
    [token, currentStep],
  );

  const handleNext = useCallback(async () => {
    if (currentStep === 13) {
      if (
        !formData.consenso_privacy ||
        !formData.consenso_veridicita ||
        !formData.consenso_trattamento
      ) {
        setErrors({
          consenso_privacy: "Obbligatorio",
          consenso_veridicita: "Obbligatorio",
          consenso_trattamento: "Obbligatorio",
        });
        return;
      }
      const consentLog = {
        privacy: formData.consenso_privacy,
        veridicita: formData.consenso_veridicita,
        trattamento: formData.consenso_trattamento,
        timestamp: new Date().toISOString(),
        ua: navigator.userAgent,
      };
      const finalData = {
        ...formData,
        data_conferma: new Date().toISOString(),
        completion_percentage: 100,
        campi_mancanti: [],
      };
      await base44.functions.invoke("submitCustomerIntake", {
        token,
        form_data: finalData,
        consent_log: consentLog,
        is_draft: false,
      });
      setPhase("submitted");
      return;
    }
    setCompletedSteps((prev) =>
      prev.includes(currentStep) ? prev : [...prev, currentStep],
    );
    await saveDraft(formData);
    setCurrentStep((prev) => Math.min(prev + 1, 13));
    window.scrollTo(0, 0);
  }, [currentStep, formData, saveDraft, token]);

  const handlePrev = useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo(0, 0);
  }, []);

  const handleGoToMissing = useCallback(() => {
    const missing = getMissingFields(formData);
    if (!missing.length) return;
    for (const step of STEPS) {
      if (step.fields.includes(missing[0])) {
        setCurrentStep(step.id);
        break;
      }
    }
  }, [formData]);

  if (phase === "loading") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (phase === "error")
    return <ErrorScreen code={errorCode} expiresAt={linkData?.expires_at} />;
  if (phase === "submitted") return <SubmittedScreen data={formData} />;
  if (phase === "welcome") {
    return (
      <WelcomeScreen
        data={formData}
        missingCount={getMissingFields(formData).length}
        onStart={() => setPhase("wizard")}
      />
    );
  }

  const percentage = calculateCompletion(formData);
  const missingCount = getMissingFields(formData).length;
  const stepProps = {
    data: formData,
    onChange: handleFieldChange,
    errors,
    metadata,
    missingCount,
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1Pratica {...stepProps} />;
      case 2:
        return <Step2Soggetto {...stepProps} />;
      case 3:
        return <Step3Anagrafica {...stepProps} />;
      case 4:
        return <Step4Residenza {...stepProps} />;
      case 5:
        return <Step5Recapiti {...stepProps} />;
      case 6:
        return <Step6Documento {...stepProps} />;
      case 7:
        return <Step7Cittadinanza {...stepProps} />;
      case 8:
        return <Step8Occupazione {...stepProps} />;
      case 9:
        return <Step9Datore {...stepProps} />;
      case 10:
        return <Step10Reddito {...stepProps} />;
      case 11:
        return <Step11SocioDemografico {...stepProps} />;
      case 12:
        return <Step12Pagamento {...stepProps} />;
      case 13:
        return <Step13Conferma {...stepProps} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar
        position="sticky"
        color="default"
        elevation={1}
        sx={{ bgcolor: "background.paper" }}
      >
        <Container maxWidth="md">
          <Toolbar
            disableGutters
            sx={{ py: 1, display: "flex", justifyContent: "space-between" }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 2,
                  bgcolor: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Shield size={18} color="white" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                  Completa la tua richiesta
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", lineHeight: 1 }}
                  noWrap
                >
                  {formData.prodotto_compagnia || "Finanziamento polizza"}
                  {linkData?.expires_at &&
                    ` · Scade il ${moment(linkData.expires_at).format(
                      "DD/MM/YY",
                    )}`}
                </Typography>
              </Box>
            </Box>
            <Chip
              label={`${percentage}%`}
              color="primary"
              size="small"
              sx={{ fontWeight: 700 }}
            />
          </Toolbar>
        </Container>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 4 }}>
        <ProgressHeader
          percentage={percentage}
          missingCount={missingCount}
          onGoToMissing={handleGoToMissing}
          lastSaved={lastSaved}
        />
        <StepIndicator
          currentStep={currentStep}
          completedSteps={completedSteps}
        />
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 4 },
            borderRadius: 4,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          {renderStep()}
          <StepNavigation
            currentStep={currentStep}
            totalSteps={13}
            onPrev={handlePrev}
            onNext={handleNext}
            onSave={() => saveDraft(formData)}
            isLastStep={currentStep === 13}
            isSaving={isSaving}
          />
        </Paper>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            textAlign: "center",
            display: "block",
            mt: 4,
            px: 2,
            fontSize: "0.625rem",
          }}
        >
          I tuoi dati sono al sicuro e verranno trattati secondo la normativa
          vigente
        </Typography>
      </Container>
    </Box>
  );
}
